import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Mic, 
  MessageSquare, 
  Shield, 
  Zap, 
  Bot,
  Lock,
  Play,
  ArrowRight,
  CheckCircle2,
  Users,
  Building2,
  LineChart,
  Heart,
  ChevronRight,
  Sparkles,
  Menu,
  X
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const navLinkClass =
  'text-slate-600 hover:text-primary-600 transition-colors font-medium rounded-md px-2 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2';

function LandingPage() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileNavOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [mobileNavOpen]);

  const handleTryDemo = () => {
    setMobileNavOpen(false);
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav
        className={`fixed w-full z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-white/80 backdrop-blur-xl shadow-sm border-b border-slate-200/50 py-4'
            : 'bg-transparent py-6'
        }`}
        aria-label="Primary"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center gap-3">
            <Link
              to="/"
              onClick={(e) => {
                setMobileNavOpen(false);
                if (window.location.pathname === '/') {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              className="flex items-center space-x-3 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/25">
                <Sparkles className="w-5 h-5 text-white" aria-hidden />
              </div>
              <span className="text-2xl font-bold tracking-tight text-slate-900">Converse AI</span>
            </Link>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className={navLinkClass}>
                Features
              </a>
              <a href="#how-it-works" className={navLinkClass}>
                How it works
              </a>
              <a href="#use-cases" className={navLinkClass}>
                Use cases
              </a>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleTryDemo}
                data-testid="nav-try-demo-btn"
                className="btn-primary hidden sm:flex items-center space-x-2 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              >
                <Play className="w-4 h-4" aria-hidden />
                <span>Try the demo</span>
              </button>
              <button
                type="button"
                className="md:hidden inline-flex items-center justify-center rounded-xl p-2.5 text-slate-700 hover:bg-slate-100/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 z-10"
                aria-expanded={mobileNavOpen}
                aria-controls="mobile-nav"
                aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
                onClick={() => setMobileNavOpen((o) => !o)}
              >
                {mobileNavOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
          <div
            id="mobile-nav"
            role="navigation"
            aria-label="Mobile"
            aria-hidden={!mobileNavOpen}
            className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
              mobileNavOpen
                ? 'max-h-72 opacity-100 mt-4 pb-1 pointer-events-auto'
                : 'max-h-0 opacity-0 pointer-events-none'
            }`}
          >
            <div className="flex flex-col gap-1 border-t border-slate-200/80 pt-4">
              <a
                href="#features"
                className={`${navLinkClass} py-2.5`}
                onClick={() => setMobileNavOpen(false)}
              >
                Features
              </a>
              <a
                href="#how-it-works"
                className={`${navLinkClass} py-2.5`}
                onClick={() => setMobileNavOpen(false)}
              >
                How it works
              </a>
              <a
                href="#use-cases"
                className={`${navLinkClass} py-2.5`}
                onClick={() => setMobileNavOpen(false)}
              >
                Use cases
              </a>
              <button
                type="button"
                onClick={handleTryDemo}
                className="btn-primary mt-2 flex items-center justify-center space-x-2 rounded-xl w-full sm:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              >
                <Play className="w-4 h-4" aria-hidden />
                <span>Try the demo</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-visible hero-gradient">
        {/* Subtle Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-96 h-96 bg-primary-200/30 rounded-full blur-3xl animate-blob" />
          <div className="absolute top-40 right-10 w-96 h-96 bg-emerald-200/30 rounded-full blur-3xl animate-blob animation-delay-2000" />
          <div className="absolute bottom-20 left-1/3 w-96 h-96 bg-teal-200/30 rounded-full blur-3xl animate-blob animation-delay-4000" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 bg-primary-50 border border-primary-200 rounded-full px-4 py-2 mb-6 max-w-full">
                <Sparkles className="w-4 h-4 text-primary-600 shrink-0" aria-hidden />
                <span className="text-sm font-medium text-primary-700 text-left leading-snug sm:whitespace-nowrap">
                  The future of corporate reporting
                </span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-slate-900 leading-tight tracking-tight mb-6">
                Reports That
                <span className="block text-primary-600">Interact</span>
              </h1>
              
              <p className="text-xl text-slate-600 mb-8 leading-relaxed max-w-xl">
                Stop sending decks that get ignored. Transform static reports into 
                <span className="font-semibold text-slate-800"> immersive AI briefings </span>
                where stakeholders listen, ask questions, and truly engage with your insights.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <button
                  type="button"
                  onClick={handleTryDemo}
                  data-testid="try-demo-btn"
                  className="btn-primary flex items-center justify-center gap-2 px-8 py-4 text-lg rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                >
                  <Play className="w-5 h-5 shrink-0" aria-hidden />
                  <span>Try the demo</span>
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/waitlist')}
                  data-testid="waitlist-btn"
                  className="btn-secondary flex items-center justify-center gap-2 px-8 py-4 text-lg rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                >
                  <span>Join the waitlist</span>
                  <ArrowRight className="w-5 h-5 shrink-0" aria-hidden />
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500">
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary-500 shrink-0" aria-hidden />
                  <span>No credit card</span>
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary-500 shrink-0" aria-hidden />
                  <span>Free demo</span>
                </span>
              </div>
            </motion.div>

            {/* Right - Interactive Demo Card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="glass-card p-8 relative z-10">
                {/* PDF Upload Card */}
                <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm mb-4">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                      <FileText className="w-6 h-6 text-primary-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">Q4 Financial Report.pdf</p>
                      <p className="text-sm text-slate-500">Uploaded just now</p>
                    </div>
                    <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    </div>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-primary-500 to-emerald-500 w-full rounded-full" />
                  </div>
                  <p className="text-sm text-emerald-600 mt-2 font-medium flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 shrink-0" aria-hidden />
                    <span>AI summary and voice generated</span>
                  </p>
                </div>

                {/* Voice Chat Preview */}
                <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl p-6 text-white">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                      <Mic className="w-4 h-4" />
                    </div>
                    <span className="font-semibold">Voice Agent Active</span>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                      <p className="text-sm opacity-90">"What was our revenue growth this quarter?"</p>
                    </div>
                    <div className="bg-white/20 rounded-lg p-3 backdrop-blur-sm">
                      <p className="text-sm">"Revenue grew 23% to $14.2M, exceeding targets by 8%..."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 bg-white rounded-xl p-3 shadow-lg border border-slate-200 animate-float z-20">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <Zap className="w-4 h-4 text-emerald-600" />
                  </div>
                  <span className="text-sm font-medium text-slate-700">Voice Summary</span>
                </div>
              </div>
              
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-3 shadow-lg border border-slate-200 animate-float animation-delay-1000 z-20">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                    <MessageSquare className="w-4 h-4 text-primary-600" />
                  </div>
                  <span className="text-sm font-medium text-slate-700">Voice Q&A</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeInUp} className="text-primary-600 font-semibold text-sm uppercase tracking-wider">
              The Challenge
            </motion.span>
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold text-slate-900 mt-3 mb-6">
              Reports Deserve More Than a Glance
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-slate-600 max-w-3xl mx-auto">
              Teams spend weeks crafting insightful reports. But stakeholders skim them in minutes—or skip them entirely.
              That leaves critical decisions at risk of being made without the full picture.
            </motion.p>
          </motion.div>

          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {[
              {
                icon: <FileText className="w-6 h-6" />,
                title: "65% of Reports Go Unread",
                description: "Busy executives can't dedicate time to 50-page documents. Your insights get filed away, never to surface when it matters most.",
                color: "bg-red-50 text-red-600"
              },
              {
                icon: <MessageSquare className="w-6 h-6" />,
                title: "Endless Follow-up Emails",
                description: "When stakeholders do engage, questions pile up fast. Hours lost to back-and-forth clarifications eat into time better spent elsewhere.",
                color: "bg-amber-50 text-amber-600"
              },
              {
                icon: <Users className="w-6 h-6" />,
                title: "Lost Stakeholder Engagement",
                description: "Static decks can't compete with dynamic content. Even the most carefully crafted narrative struggles to hold attention on a flat page.",
                color: "bg-violet-50 text-violet-600"
              }
            ].map((item, index) => (
              <motion.div key={index} variants={fadeInUp} className="glass-card p-8 text-center">
                <div className={`w-14 h-14 ${item.color} rounded-xl flex items-center justify-center mx-auto mb-5`}>
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-primary-600 font-semibold text-sm uppercase tracking-wider">The Solution</span>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-3 mb-6">
                Give Your Reports a Voice.
                <span className="block mt-1">Make them interactive.</span>
              </h2>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Converse AI transforms your static reports into interactive AI briefings. Stakeholders don't just read—they{' '}
                <span className="font-semibold text-slate-800">listen, ask, and engage</span>.
              </p>

              <div className="space-y-6">
                {[
                  {
                    icon: <Play className="w-5 h-5" />,
                    title: "2-Minute Voice Summary",
                    description: "AI distills your report into a professional voice briefing. Perfect for busy commutes or quick catch-ups."
                  },
                  {
                    icon: <Mic className="w-5 h-5" />,
                    title: "Voice-to-Voice Q&A",
                    description: "Stakeholders ask questions naturally by speaking. AI answers instantly from your document—no hallucinations."
                  },
                  {
                    icon: <Shield className="w-5 h-5" />,
                    title: "Context-Aware Intelligence",
                    description: "AI only answers from your document. For out-of-scope questions, it drafts an email to connect them with you."
                  }
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0 text-primary-600">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-1">{item.title}</h3>
                      <p className="text-slate-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl border border-slate-200">
                <img 
                  src="https://images.unsplash.com/photo-1770048532712-4fde5ef7eb90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBleGVjdXRpdmUlMjBnbGFzcyUyMG9mZmljZSUyMG1lZXRpbmd8ZW58MHx8fHwxNzc0MDE2OTEzfDA&ixlib=rb-4.1.0&q=85"
                  alt="Modern corporate meeting"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <p className="text-lg font-semibold">Enterprise-Grade AI Briefings</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-24 bg-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeInUp} className="text-primary-600 font-semibold text-sm uppercase tracking-wider">
              Features
            </motion.span>
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold text-slate-900 mt-3 mb-6">
              Everything You Need
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-slate-600 max-w-3xl mx-auto">
              Powerful tools to transform how your organization communicates through reports
            </motion.p>
          </motion.div>

          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {[
              {
                icon: <Zap className="w-6 h-6" />,
                title: "Instant AI Briefings",
                description: "Advanced AI distills your entire report into a crisp executive summary—delivering every key insight in under 2 minutes.",
                color: "bg-primary-50 text-primary-600"
              },
              {
                icon: <Play className="w-6 h-6" />,
                title: "Voice Narration",
                description: "A natural-sounding AI voice brings your briefings to life, making it easy for stakeholders to listen on the go.",
                color: "bg-emerald-50 text-emerald-600"
              },
              {
                icon: <Bot className="w-6 h-6" />,
                title: "Conversational Agent",
                description: "Stakeholders can ask anything about the report—by typing or speaking—and get accurate, instant answers. Like having your smartest analyst on call, 24/7.",
                color: "bg-violet-50 text-violet-600"
              },
              {
                icon: <Users className="w-6 h-6" />,
                title: "Personalized Stakeholder Briefings",
                description: "Every stakeholder gets what's relevant to them. Converse AI tailors briefings based on role and designation—so the CFO sees what they care about, and so does the ops lead.",
                color: "bg-amber-50 text-amber-600"
              },
              {
                icon: <Shield className="w-6 h-6" />,
                title: "Grounded in Your Data",
                description: "The AI answers strictly from your document. No hallucinations, no guesswork—just reliable insights your leadership can trust.",
                color: "bg-slate-100 text-slate-600"
              },
              {
                icon: <MessageSquare className="w-6 h-6" />,
                title: "Secure Sharing",
                description: "Generate unique, expirable links with access tracking. Always know who's viewed your report and when.",
                color: "bg-blue-50 text-blue-600"
              },
              {
                icon: <Lock className="w-6 h-6" />,
                title: "Enterprise-Grade Security",
                description: "All data stays within your organization. Converse AI never stores or transmits your reports outside your enterprise environment—your information stays exactly where it belongs.",
                color: "bg-rose-50 text-rose-600",
                highlight: true
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className={`glass-card glass-card-hover p-8 group ${
                  feature.highlight
                    ? 'md:col-span-2 lg:col-span-3 lg:flex lg:flex-row lg:items-start lg:gap-8 lg:text-left'
                    : ''
                }`}
              >
                <div
                  className={`w-14 h-14 ${feature.color} rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 ${
                    feature.highlight ? 'mb-5 lg:mb-0 flex-shrink-0' : 'mb-5'
                  }`}
                >
                  {feature.icon}
                </div>
                <div className={feature.highlight ? 'min-w-0 flex-1' : 'contents'}>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 bg-gradient-to-b from-slate-50 to-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeInUp} className="text-primary-600 font-semibold text-sm uppercase tracking-wider">
              How it works
            </motion.span>
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold text-slate-900 mt-3 mb-6">
              Three steps to engagement
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-slate-600 max-w-3xl mx-auto">
              Get started in minutes—no technical expertise required
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Upload Your Report",
                description: "Drop your PDF and add a title. Our AI extracts all text and prepares it for analysis.",
                gradient: "from-primary-500 to-primary-600"
              },
              {
                step: "02",
                title: "Generate Briefing",
                description: "One click creates a 2-minute AI summary and converts it to professional voice audio.",
                gradient: "from-primary-600 to-emerald-600"
              },
              {
                step: "03",
                title: "Share & Engage",
                description: "Create a secure link. Recipients listen, ask questions via voice, and get instant answers.",
                gradient: "from-emerald-600 to-teal-600"
              }
            ].map((item, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="relative"
              >
                <div className={`bg-gradient-to-br ${item.gradient} rounded-2xl p-8 text-white h-full`}>
                  <div className="text-5xl font-bold opacity-30 mb-4">{item.step}</div>
                  <h3 className="text-2xl font-bold mb-3">{item.title}</h3>
                  <p className="opacity-90">{item.description}</p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ChevronRight className="w-8 h-8 text-slate-300" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section id="use-cases" className="py-24 bg-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeInUp} className="text-primary-600 font-semibold text-sm uppercase tracking-wider">
              Use cases
            </motion.span>
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold text-slate-900 mt-3 mb-6">
              Built for Enterprise Teams
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-slate-600 max-w-3xl mx-auto">
              Trusted by forward-thinking organizations across industries
            </motion.p>
          </motion.div>

          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 gap-8"
          >
            {[
              {
                icon: <Building2 className="w-6 h-6" />,
                title: "Executive Teams",
                description: "Transform board reports, quarterly reviews, and strategic plans into digestible briefings that drive decisions.",
                color: "bg-primary-50 text-primary-600"
              },
              {
                icon: <Users className="w-6 h-6" />,
                title: "Consulting Firms",
                description: "Deliver client reports with interactive Q&A. Reduce follow-up meetings by 60% while improving comprehension.",
                color: "bg-violet-50 text-violet-600"
              },
              {
                icon: <LineChart className="w-6 h-6" />,
                title: "Financial Services",
                description: "Make investment reports and market analysis accessible via voice for clients on-the-go.",
                color: "bg-emerald-50 text-emerald-600"
              },
              {
                icon: <Heart className="w-6 h-6" />,
                title: "Healthcare",
                description: "Share research findings and patient outcome reports with voice-enabled accessibility for busy practitioners.",
                color: "bg-rose-50 text-rose-600"
              }
            ].map((item, index) => (
              <motion.div key={index} variants={fadeInUp} className="glass-card p-8 flex items-start space-x-5">
                <div className={`w-14 h-14 ${item.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  {item.icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-primary-700 to-emerald-700" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-72 h-72 bg-white rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Reports?
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
              Join innovative teams already using Converse AI to make their reports come alive. 
              Start engaging stakeholders today.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button
                type="button"
                onClick={handleTryDemo}
                data-testid="try-demo-cta-btn"
                className="bg-white text-primary-700 px-10 py-4 rounded-xl font-bold text-lg hover:bg-slate-50 transition-colors shadow-xl flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary-700"
              >
                <Play className="w-5 h-5 shrink-0" aria-hidden />
                <span>Try the demo</span>
              </button>
              <button
                type="button"
                onClick={() => navigate('/waitlist')}
                data-testid="waitlist-cta-btn"
                className="bg-white/10 backdrop-blur-sm border-2 border-white/50 text-white px-10 py-4 rounded-xl font-bold text-lg hover:bg-white/20 transition-colors flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary-700"
              >
                <span>Join the waitlist</span>
                <ArrowRight className="w-5 h-5 shrink-0" aria-hidden />
              </button>
            </div>
            <p className="mt-8 text-white/70 text-sm">
              No credit card required · 2-minute setup · Cancel anytime
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 pt-16 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-12 lg:flex-row lg:justify-between lg:items-start lg:gap-16">
            <div className="max-w-md shrink-0">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center shrink-0">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-white leading-tight">Converse AI</span>
              </div>
              <p className="text-sm leading-relaxed text-slate-400">
                Transform static reports into interactive AI briefings with voice-enabled conversations.
                The future of corporate communication.
              </p>
            </div>

            <div className="flex flex-col gap-12 sm:flex-row sm:gap-20 lg:gap-24 xl:gap-32 lg:shrink-0">
              <div className="min-w-[11rem]">
                <h4 className="text-white font-semibold mb-5">Product</h4>
                <ul className="space-y-4 text-sm leading-relaxed">
                  <li>
                    <a
                      href="#features"
                      className="inline-block py-0.5 hover:text-white transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                    >
                      Features
                    </a>
                  </li>
                  <li>
                    <a
                      href="#how-it-works"
                      className="inline-block py-0.5 hover:text-white transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                    >
                      How it works
                    </a>
                  </li>
                  <li>
                    <a
                      href="#use-cases"
                      className="inline-block py-0.5 hover:text-white transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                    >
                      Use cases
                    </a>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={handleTryDemo}
                      className="inline-block py-0.5 hover:text-white transition-colors text-left rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                    >
                      Try the demo
                    </button>
                  </li>
                </ul>
              </div>

              <div className="min-w-[11rem]">
                <h4 className="text-white font-semibold mb-5">Contact</h4>
                <ul className="space-y-4 text-sm leading-relaxed">
                  <li>
                    <a
                      href="https://www.sachinkothamasa.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block py-0.5 break-all sm:break-normal hover:text-white transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                    >
                      www.sachinkothamasa.com
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-800 mt-14 pt-10 text-center text-sm text-slate-500">
            <p className="leading-relaxed">
              © 2026 Converse AI. All rights reserved. Building the future of corporate communication.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
